class ModernNumberHelpers {
  static number_to_currency(number, opts = {}) {
    const _unit = opts.unit || '$';
    const _precision = opts.precision !== undefined ? opts.precision : 2;
    const _unit_pos = opts.unit_position || 'start';
    const _delimiter = opts.delimiter || ',';
    const _separator = opts.separator || '.';

    // Handle non-numeric values
    if (isNaN(number)) {
      return number;
    }

    // For custom units or non-USD currencies, fall back to manual formatting
    if (_unit !== '$' || _delimiter !== ',' || _separator !== '.') {
      return this._formatCurrencyManually(number, opts);
    }

    // Use native Intl.NumberFormat for USD with standard formatting
    try {
      const formatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: _precision,
        maximumFractionDigits: _precision
      }).format(number);

      // Handle unit position
      if (_unit_pos === 'end') {
        return formatted.replace('$', '') + _unit;
      }
      
      return formatted;
    } catch (error) {
      // Fallback to manual formatting if Intl fails
      return this._formatCurrencyManually(number, opts);
    }
  }

  static _formatCurrencyManually(number, opts = {}) {
    const _unit = opts.unit || '$';
    const _precision = opts.precision !== undefined ? opts.precision : 2;
    const _separator = opts.separator || '.';
    const _delimiter = opts.delimiter || ',';
    const _unit_pos = opts.unit_position || 'start';

    let sign = '';
    let result;

    if (isNaN(number)) {
      result = number;
    } else {
      number = parseFloat(number);
      if (number < 0) { sign = '-'; }
      const fixedWidth = Math.abs(number).toFixed(_precision);
      const delimited = this.number_with_delimiter(fixedWidth, {delimiter: _delimiter});
      result = delimited.split('.').join(_separator);
    }

    if (_unit_pos === 'end') {
      return `${sign}${result}${_unit}`;
    } else {
      return `${sign}${_unit}${result}`;
    }
  }

  static number_with_delimiter(number, opts = {}) {
    const _separator = opts.separator || '.';
    const _delimiter = opts.delimiter || ',';

    // Use native Intl.NumberFormat for standard delimiters
    if (_delimiter === ',' && _separator === '.') {
      try {
        return new Intl.NumberFormat('en-US', {
          useGrouping: true,
          minimumFractionDigits: 0,
          maximumFractionDigits: 20 // Preserve existing decimals
        }).format(number);
      } catch (error) {
        // Fallback to manual formatting
      }
    }

    // Manual formatting for custom delimiters/separators
    const numberStr = number.toString().split(".");
    let integer = numberStr[0];
    const decimal = numberStr[1] || '';
    const finalSeparator = decimal ? _separator : '';

    const rgx = /(\d+)(\d{3})/;
    if (_delimiter) {
      while (rgx.test(integer)) {
        integer = integer.replace(rgx, "$1" + _delimiter + "$2");
      }
    }

    return `${integer}${finalSeparator}${decimal}`;
  }

  static number_with_precision(number, opts = {}) {
    const _precision = opts.precision !== undefined ? opts.precision : 3;
    const _delimiter = opts.delimiter || ',';
    const _separator = opts.separator || '.';
    const _significant = opts.significant !== undefined ? opts.significant : false;
    const _strip_insignificant_zeros = opts.strip_insignificant_zeros !== undefined ? opts.strip_insignificant_zeros : false;

    // For standard formatting, try native Intl first
    if (!_significant && _delimiter === ',' && _separator === '.' && !_strip_insignificant_zeros) {
      try {
        return new Intl.NumberFormat('en-US', {
          useGrouping: true,
          minimumFractionDigits: _precision,
          maximumFractionDigits: _precision
        }).format(number);
      } catch (error) {
        // Fallback to original implementation
      }
    }

    // Complex formatting - use original implementation
    return this._formatWithPrecisionManually(number, opts);
  }

  static _formatWithPrecisionManually(float, opts = {}) {
    const _precision = opts.precision !== undefined ? opts.precision : 3;
    const _delimiter = opts.delimiter || ',';
    const _separator = opts.separator || '.';
    const _significant = opts.significant !== undefined ? opts.significant : false;
    const _strip_insignificant_zeros = opts.strip_insignificant_zeros !== undefined ? opts.strip_insignificant_zeros : false;
    const _skip_empty_fractionals = opts.strip_empty_fractional_parts;

    // Break number into inspectable pieces
    let number = float.toString().split('.');
    let integer = number[0];
    let decimal = number[1] || '';

    // Significant Digits need rounding to match number of sig digits
    let rnd;
    if (_significant) {
      rnd = _precision - integer.length;
    } else {
      rnd = _precision;
    }

    // Make sure rounding is not to a negative place
    if (rnd < 1) { rnd = 0; }

    // Round
    const multiple = Math.pow(10, rnd);
    let rounded;
    if (multiple > 1) {
      rounded = Math.round(float * multiple) / multiple;
    } else {
      rounded = float;
    }

    // Break number into inspectable pieces
    number = rounded.toString().split('.');
    integer = number[0];
    decimal = number[1] || '';

    // Pad to _precision
    decimal = parseFloat(`0.${decimal}`).toFixed(_precision);
    decimal = decimal.toString().split('.');
    decimal = decimal[1] || '';

    // Reconstitute the number with correct decimal
    number = `${integer}.${decimal}` * 1;
    const num_array = number.toString().split('');
    const num_lngth = num_array.length;

    // Count Non-zero Digits
    let i = 0, sigs = 0;
    while (i < num_lngth) {
      if (!(num_array[i] === '.' || num_array[i] === '0')) { sigs++; }
      i++;
    }

    // Significant Digits
    if (_significant && sigs >= _precision && _precision > 0) {
      const significant = number.toPrecision(_precision) * 1;
      const significantArray = significant.toString().split('.');
      integer = significantArray[0];
      decimal = significantArray[1] || '';
    }

    // Delimiter Integer
    integer = this.number_with_delimiter(integer, {delimiter: _delimiter});

    // Strip Insignificant Digits
    if (_strip_insignificant_zeros) {
      const dlen = decimal.length;
      let newlen = dlen;

      while (newlen > 0 && decimal[newlen - 1] === '0') {
        newlen = newlen - 1;
      }

      if (newlen === 0) {
        decimal = '';
      } else if (newlen !== dlen) {
        decimal = decimal.slice(0, newlen);
      }
    }

    if (_skip_empty_fractionals) {
      i = 0;
      let zcount = 0;
      const num_array = decimal.split('');
      const dlen = decimal.length;
      while (i < dlen) {
        if (num_array[i] === '0') { zcount++; }
        i++;
      }
      if (zcount === dlen) {
        decimal = '';
      }
    }

    // Remove separator if no decimal
    const finalSeparator = decimal ? _separator : '';

    return `${integer}${finalSeparator}${decimal}`;
  }

  static number_to_human(number, opts = {}) {
    const _precision = opts.precision !== undefined ? opts.precision : 3;
    const _separator = opts.separator || '.';
    const _significant = opts.significant !== undefined ? opts.significant : true;
    const _delimiter = opts.delimiter || ',';
    const _strip_insignificant_zeros = opts.strip_insignificant_zeros !== undefined ? opts.strip_insignificant_zeros : false;
    const _space_label = opts.space_label === false ? '' : ' ';

    // For standard English formatting, try native Intl first
    if (_delimiter === ',' && _separator === '.' && _space_label === ' ') {
      try {
        // Use compact notation for large numbers
        if (Math.abs(number) >= 1000) {
          return new Intl.NumberFormat('en-US', {
            notation: 'compact',
            maximumFractionDigits: _precision
          }).format(number);
        }
      } catch (error) {
        // Fallback to manual formatting
      }
    }

    // Manual formatting for custom options or fallback
    return this._formatHumanManually(number, opts);
  }

  static _formatHumanManually(float, opts = {}) {
    const _precision = opts.precision !== undefined ? opts.precision : 3;
    const _separator = opts.separator || '.';
    const _significant = opts.significant !== undefined ? opts.significant : true;
    const _delimiter = opts.delimiter || ',';
    const _strip_insignificant_zeros = opts.strip_insignificant_zeros !== undefined ? opts.strip_insignificant_zeros : false;
    const _space_label = opts.space_label === false ? '' : ' ';
    const _labels = {
      thousand: (opts.labels && opts.labels.thousand) || 'Thousand',
      million: (opts.labels && opts.labels.million) || 'Million',
      billion: (opts.labels && opts.labels.billion) || 'Billion',
      trillion: (opts.labels && opts.labels.trillion) || 'Trillion',
      quadrillion: (opts.labels && opts.labels.quadrillion) || 'Quadrillion'
    };

    // Remove the sign of the number for easier comparison
    const abs_float = Math.abs(float);

    let denom, label;
    // Less than Thousand does not need text or insignificant digits
    if (abs_float < Math.pow(10, 3)) {
      denom = 1;
      label = false;
    } else if (abs_float >= Math.pow(10, 3) && abs_float < Math.pow(10, 6)) {
      denom = Math.pow(10, 3);
      label = _labels.thousand;
    } else if (abs_float >= Math.pow(10, 6) && abs_float < Math.pow(10, 9)) {
      denom = Math.pow(10, 6);
      label = _labels.million;
    } else if (abs_float >= Math.pow(10, 9) && abs_float < Math.pow(10, 12)) {
      denom = Math.pow(10, 9);
      label = _labels.billion;
    } else if (abs_float >= Math.pow(10, 12) && abs_float < Math.pow(10, 15)) {
      denom = Math.pow(10, 12);
      label = _labels.trillion;
    } else if (abs_float >= Math.pow(10, 15)) {
      denom = Math.pow(10, 15);
      label = _labels.quadrillion;
    }

    // Process the number into a presentable format
    const number = float / denom;
    const precise = this.number_with_precision(number, {
      precision: _precision,
      significant: _significant,
      delimiter: label === 'Quadrillion' ? '' : _delimiter,
      separator: _separator,
      strip_insignificant_zeros: !label ? true : _strip_insignificant_zeros
    });

    // No label needed for less than thousand
    if (label) {
      return `${precise}${_space_label}${label}`;
    } else {
      return precise;
    }
  }

  static number_to_human_size(number, opts = {}) {
    const _precision = opts.precision !== undefined ? opts.precision : 3;

    // For standard formatting, try native Intl first
    try {
      // Convert to bytes if needed (original logic seems to do power of 10 to bytes conversion)
      let bytes = number;
      if (bytes > 1000) { bytes = bytes / 1.024; }
      if (bytes > 1000000) { bytes = bytes / 1.024; }
      if (bytes > 1000000000) { bytes = bytes / 1.024; }
      if (bytes > 1000000000000) { bytes = bytes / 1.024; }

      // Use native Intl for bytes formatting
      return new Intl.NumberFormat('en-US', {
        style: 'unit',
        unit: 'byte',
        unitDisplay: 'short',
        maximumFractionDigits: _precision
      }).format(bytes);
    } catch (error) {
      // Fallback to manual formatting if Intl unit style not supported
      return this._formatHumanSizeManually(number, opts);
    }
  }

  static _formatHumanSizeManually(float, opts = {}) {
    const _precision = opts.precision !== undefined ? opts.precision : 3;
    const _separator = opts.separator || '.';
    const _significant = opts.significant !== undefined ? opts.significant : true;
    const _delimiter = opts.delimiter || ',';
    const _strip_insignificant_zeros = opts.strip_insignificant_zeros !== undefined ? opts.strip_insignificant_zeros : false;

    // Power of 10 to Bytes Converter
    if (float > 1000) { float = float / 1.024; }
    if (float > 1000000) { float = float / 1.024; }
    if (float > 1000000000) { float = float / 1.024; }
    if (float > 1000000000000) { float = float / 1.024; }

    // Remove the sign of the number for easier comparison
    const abs_float = Math.abs(float);

    let denom, label;
    // Less than Thousand does not need text or insignificant digits
    if (abs_float < Math.pow(10, 3)) {
      denom = 1;
      label = 'Bytes';
    } else if (abs_float >= Math.pow(10, 3) && abs_float < Math.pow(10, 6)) {
      denom = Math.pow(10, 3);
      label = 'KB';
    } else if (abs_float >= Math.pow(10, 6) && abs_float < Math.pow(10, 9)) {
      denom = Math.pow(10, 6);
      label = 'MB';
    } else if (abs_float >= Math.pow(10, 9) && abs_float < Math.pow(10, 12)) {
      denom = Math.pow(10, 9);
      label = 'GB';
    } else if (abs_float >= Math.pow(10, 12) && abs_float < Math.pow(10, 15)) {
      denom = Math.pow(10, 12);
      label = 'TB';
    }

    // Process the number into a presentable format
    const number = float / denom;

    const precise = this.number_with_precision(number, {
      precision: _precision,
      significant: _significant,
      delimiter: _delimiter,
      separator: _separator,
      strip_insignificant_zeros: label === 'Bytes' ? true : _strip_insignificant_zeros
    });

    return `${precise} ${label}`;
  }

  static number_to_phone(number, opts = {}) {
    const _area_code = opts.area_code !== undefined ? opts.area_code : false;
    const _delimiter = opts.delimiter || '-';
    const _country_code = opts.country_code !== undefined ? opts.country_code : false;
    const _extension = opts.extension !== undefined ? opts.extension : false;

    // Not a numerical value
    if (isNaN(number)) { return number; }

    const str = number.toString();
    const lng = str.length;

    // Last Four Digits
    const last = str.substr(lng - 4, lng);

    let first;
    if (lng < 8) {
      first = str.substr(0, 3);
    } else {
      const firstPart = str.substr(0, 3);
      const second = str.substr(3, 3);

      // Area Code
      if (_area_code) {
        first = `(${firstPart}) ${second}`;
      } else {
        first = `${firstPart}${_delimiter}${second}`;
      }
    }

    // Extension Code
    const extensionCode = _extension ? ` x ${opts.extension}` : '';

    // Country Code
    const countryCode = _country_code ? `+${_country_code}${_delimiter}` : '';

    return `${countryCode}${first}${_delimiter}${last}${extensionCode}`;
  }

  static number_to_percentage(number, opts = {}) {
    const _precision = opts.precision !== undefined ? opts.precision : 3;

    // Use native Intl.NumberFormat for percentage formatting
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'percent',
        minimumFractionDigits: _precision,
        maximumFractionDigits: _precision
      }).format(number / 100); // Intl expects decimal (0.1234 for 12.34%)
    } catch (error) {
      // Fallback to manual formatting
      const _separator = opts.separator || '.';
      const _significant = opts.significant !== undefined ? opts.significant : false;
      const _delimiter = opts.delimiter || '';
      const _strip_insignificant_zeros = opts.strip_insignificant_zeros !== undefined ? opts.strip_insignificant_zeros : false;

      if (!isNaN(number)) {
        number = this.number_with_precision(number, {
          precision: _precision,
          significant: _significant,
          delimiter: _delimiter,
          separator: _separator,
          strip_insignificant_zeros: _strip_insignificant_zeros
        });
      }

      return `${number}%`;
    }
  }
}

// Immediately assign to global so it's available during bundling
window.NumberHelpers = ModernNumberHelpers;
window.ModernNumberHelpers = ModernNumberHelpers;

if (typeof module !== 'undefined' && typeof module.exports !== 'undefined') {
  module.exports = ModernNumberHelpers;
}